import { GetServerSideProps } from "next";
import { allHeadings } from "@/config/constants";
import { cleanOcrText } from "@/utils";
import MathJax from "@/components/MathJaxText";
import { getUserAskedQuestionDataByEntityId } from "@/helpers/api/service/ExternalQuestion";
import Playlist from "@/components/Playlist";
import CollapsiblePlaylist from "@/components/Playlist/Collapsible";
import Heading from "@/components/Badge/Heading";
import { TypesetOptions, TypesetText } from "@/helpers/typeset";
import MetaSeo from "@/components/Head/Meta";
import Solution from "@/components/TextSolution/Solution";
import Options from "@/components/TextSolution/Options";
import { IUserAskedQuestionData } from "./interfaces";

const UserAskedQuestion = (props: IUserAskedQuestionData) => {
  const hasOptions = !!(props.options?.A && props.options?.B && props.options?.C && props.options?.D);
  const languageHeadings = allHeadings["default"];

  return (
    <div id="webpage">
      <MetaSeo title={props.questionRaw} description={props.solution} />
      <div id="article" className="body-content mt-0 flex flex-col sm:flex-row">
        <div id="focus-container" className="flex items-center flex-col basis-3/4 grow" >
          <div id="abf-container" className="flex flex-wrap justify-center w-full">
            <div id="qa-container" className="flex flex-col items-center grow">
              <div id="speakableSection" className="relative w-full overflow-x-auto flex flex-col overflow-y-hidden">
                <section className="my-1">
                  <Heading headingText="Question" />
                  <h1 id="ocr-text" className="text-base lg:text-lg mt-2 mb-0">
                    <MathJax ocrText={props.question} className="!w-full inline-block max-w-[calc(100vw-1rem)] overflow-x-auto" />
                  </h1>
                  {
                    hasOptions &&
                    <Options
                      headingText={languageHeadings["text_solution"]}
                      textSolutionMathJax={{
                        opt1: props.options?.A,
                        opt2: props.options?.B,
                        opt3: props.options?.C,
                        opt4: props.options?.D,
                        answer: props.options?.correctOptions?.[0],
                      }}
                    />
                  }
                </section>
                <CollapsiblePlaylist id={props.playlist.id} resources={props.playlist.resources} />
              </div>
              {
                props.solution && <Solution
                  headingText={languageHeadings["text_solution"]}
                  textSolutionMathJax={{
                    solution: props.solution,
                    answer: props.answer,
                  }}
                />
              }
            </div>
          </div>
          {/* <div id="btf-container" className="flex flex-col w-full">
            <Similar heading={"Related Solutions"} data={videoData.matched_questions.filter(x => !x.type)} similarMathJax={props.similarMathJax} />
          </div> */}
        </div>
        <div id="playlist-container" className="flex-col grow hidden lg:flex basis-1/4 pl-8 lg:max-w-sm space-y-4 sm:mt-4">
          <Playlist data={{
            header: true,
            ...props.playlist,
          }} />
        </div>
      </div>
    </div>
  );
}


export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const entityId = ctx.query.entityId as string;

  if (!entityId) {
    return {
      notFound: true,
    }
  }

  const props = await getUserAskedQuestionDataByEntityId(entityId);

  if (!props.entityId) {
    return {
      notFound: true,
    }
  }

  const [question, options, solution, similar] = await Promise.all([
    TypesetText(props.question, "mml"),
    TypesetOptions(props.options?.A || "", props.options?.B || "", props.options?.C || "", props.options?.D || "", "mml"),
    TypesetText(props.solution || "", "mml"),
    Promise.all((props.similarQuestions || []).map(x => TypesetText(x.ocr_text, "mml"))),
  ]);

  props.questionRaw = cleanOcrText(props.question);
  props.question = question;
  if (props.options) {
    props.options.A = options[0];
    props.options.B = options[1];
    props.options.C = options[2];
    props.options.D = options[3];
  }
  props.solution = solution;
  props.similarQuestions?.forEach((x, i) => x.ocr_text = similar[i]);

  return {
    props,
  }
}

export default UserAskedQuestion;